// /app/notes/:chapterId — the Notes screen (BRIEFS C4.3, design _v2/design/Notes.dc.html).
//
// Top strip: breadcrumb; Highlight and Add stick-on (C5.1); Search box
// (→ /app/search, C4.4). Left: the outline (units with NCERT page superscripts; unnumbered
// headings italic; added topics with an "Extra" pill). Right: the notes — topic h2 with
// "NCERT p. N", lower levels with the number only; points as nested lists with the colour
// classes of the notes file; "Extra" pill on extra points; NCERT figures (click to enlarge);
// a small "Videos" block at the start of the unit — one text + icon line per video, concept
// videos first, then question videos (C12.1, D-106; VideoModal plays one over the notes).
//
// Highlights (C5.1): select words → the small "Highlight" button by the selection (or the
// Highlight button in the top strip) saves one highlight per point touched (offsets in the
// point's plain text, components/app/marks.js); click a highlight → "Remove highlight".
// Dictionary (C5.2): selecting one word (double-click or drag) looks it up — chapter
// meaning, else general meaning, else the AI writes one (server side, cached) — and shows
// the "Word meaning" box (English + Hindi) with the Highlight button in it. A short term of
// 2–3 words is looked up only in the prepared dictionary (no AI call). Sends dict_lookup.
// Stick-ons (C5.1): a round button beside every unit title adds one; each saved stick-on is
// a small "My stick-on" button there that opens the yellow note (hover, focus or click)
// with Edit / Remove. "Add stick-on" in the top strip adds one to the unit being read.
//
// PAGES (owner 2026-10-09): one unit per page — coloured title band, numbered point cards,
// figures as cards, "Remember from this page" (exam lines + numbers), Previous / Next (also
// the ← → keys). The points of a page appear ONE BY ONE, each after the time needed to read
// the one before (by its length); Pause / Next line (Space) / Show all. The outline lists the
// pages with a tick on the ones read on this laptop (components/app/notePage.js).
// Deep link: ?at=<point or unit code> (also ?unit=, used by Home) opens that page with all
// points shown, scrolls to the point and flashes it. Without one, the page read last here.
// A page counts as opened after one second: notes_open; leaving it sends notes_read.
//
// Notes opened from a question (C7.5): the same screen with a `qmode` object (made by
// pages/app/AppTestNotes.js) — full screen, the dark pinned "Back to Question N" bar above
// the top strip, no outline column, no search box; the linked point (or unit title) is boxed
// ("The answer to Question N is here" / "Read this part for Question N") and the button
// "I have read it. Back to Question N" follows that part. Highlight, stick-on, dictionary and
// video work as usual. The unit read here is not remembered as "last read" (that stays the
// place of his normal reading).
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { track, useActivityArea } from '../../components/app/activity';
import {
  addHighlight, addSticky, editSticky, getChapterNotes, lookupWord, removeHighlight, removeSticky,
} from '../../api/v2';
import { markHtml, offsetsInPoint } from '../../components/app/marks';
import VideoModal from '../../components/app/VideoModal';
import VideoLines from '../../components/app/VideoLines';
import { IconSearch } from '../../components/app/icons';
import { AnswerBox, BackBar, EndBackButton, boxLabel } from '../../components/app/NotesBack';
import { C, FONT } from '../../components/app/theme';
import { LoadError, Loading } from '../../components/app/LoadState';
import { pinnedTop } from '../../components/app/AppLayout';
import {
  BeyondTag, ColourKey, ExamTag, IconCheck, PAGE_CSS, Pager, RecapCard, decorateHtml, hasExamLine, plainShort, recapOf,
} from '../../components/app/notePage';
import {
  SlidePresenter, SlideViewer, ViewSwitch, chapterCode, chapterSlides, readView, saveView, slidesOf, useChapterSlides,
} from '../../components/app/SlideDeck';

const STRIP_H = 65;           // height of the sticky top strip (44 px buttons + padding)
const COMMIT_MS = 1000;       // a unit counts as "opened" after 1 s on screen
const LAST_UNIT_KEY = (chapterId) => `v2_notes_last_unit:${chapterId}`;
const SEEN_KEY = (chapterId) => `v2_notes_seen:${chapterId}`;

// Colour classes and tables inside the notes HTML (the html comes sanitised from the server).
const NOTES_CSS = `
.v2-notes .c-red{color:${C.noteRed};font-weight:700}
.v2-notes .c-blue{color:${C.noteBlue};font-weight:700}
.v2-notes .c-green{color:${C.noteGreen};font-weight:700}
.v2-notes .c-mark{background:${C.marker}}
.v2-notes .v2-pt{overflow-x:auto}
.v2-notes table{border-collapse:collapse;margin:6px 0;font-size:15px;line-height:1.35}
.v2-notes th,.v2-notes td{border:1px solid #CBD5E1;padding:4px 8px;text-align:left;vertical-align:top}
.v2-notes th{background:#F1F5F9;font-weight:700}
.v2-notes sup,.v2-notes sub{line-height:0}
@keyframes v2flash{0%{background:#FDE68A}70%{background:#FEF3C7}100%{background:transparent}}
.v2-flash{animation:v2flash 2.6s ease-out;border-radius:6px}
.v2-outline-item:hover{background:#EEF2FF}
.v2-notes mark.v2-hl{background:#FDE68A;color:inherit;border-radius:2px;padding:0;cursor:pointer;box-shadow:inset 0 -2px 0 #EAB308}
.v2-notes mark.v2-hl-review{background:transparent;box-shadow:none;outline:2px dashed #B45309;outline-offset:2px}
.v2-notes mark.v2-hl-review mark.v2-hl{outline:0}
.v2-sticky-pop{display:none}
.v2-sticky-wrap:hover .v2-sticky-pop,.v2-sticky-wrap:focus-within .v2-sticky-pop,.v2-sticky-wrap.open .v2-sticky-pop{display:flex}
`;
const STICKY_MAX = 500;

const ID = { unit: (code) => `u-${code}`, point: (code) => `p-${code}` };

function unitLabel(u) {
  return u.number ? `${u.number} ${u.title}` : u.title;
}

// ------------------------------------------------------------------ small pieces

function ExtraPill() {
  return (
    <span style={{
      display: 'inline-block', padding: '0 8px', border: '1px solid #CBD5E1', borderRadius: 10,
      fontFamily: FONT.ui, fontSize: 11, lineHeight: '18px', color: C.muted, fontWeight: 500,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>Extra</span>
  );
}

const IconHighlight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4l6 6-9 9H5v-6z" /><path d="M4 21h16" /></svg>
);
const IconSticky = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16v10l-6 6H4z" /><path d="M14 20v-6h6" /></svg>
);

const stripBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, padding: 0,
  border: '1px solid #CBD5E1', borderRadius: 9, background: C.surface, color: C.text, cursor: 'pointer',
};

// The one slim line above the notes (owner 2026-10-31: "the content is the hero, anything else
// is a distraction"): breadcrumb + the page's number and title on the left; then, as small
// icons, PYQ chip, stick-ons, Theory | Slides, Previous / Next, Highlight, Add stick-on,
// Search, and "4 / 32". A 2 px progress line runs along its bottom. `top`: where the sticky
// strip stops — under the top menu bar (C11.1) and the desktop title bar (C9.1 FIX F1).
function TopStrip({
  chapter, onHighlight, onAddSticky, sticky = true, search = true, top = 0,
  unit = null, parentLabel = null, pageNo = 0, pageCount = 0, prev = null, next = null, onGo, labelOf,
  stickies = [], ops = null, deck = null, view = 'theory', onView, slidesCount = 0,
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  const submit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (v.length >= 2) navigate(`/app/search?q=${encodeURIComponent(v)}`);
  };
  const special = unit ? (unit.kind === 'summary' ? 'Summary' : unit.kind === 'exercises' ? 'Exercises' : unit.kind === 'intro' ? 'Start' : null) : null;
  const accent = unit && unit.kind === 'exercises' ? '#0F766E' : unit && unit.kind === 'summary' ? '#B45309' : C.primary;
  return (
    <div data-testid="v2-page-hero" style={{
      position: sticky ? 'sticky' : 'relative', ...(sticky ? { top, zIndex: 5 } : {}), boxSizing: 'border-box', minHeight: STRIP_H,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '6px 18px 8px',
      background: C.surface, borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div data-testid="v2-notes-breadcrumb" style={{ fontFamily: FONT.ui, fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Link to="/app/notes" style={{ color: C.muted }}>Class {chapter.class_level}</Link>
          {' · '}Chapter {chapter.ncert_no}{' · '}{chapter.title}
          {parentLabel && <>{' › '}{parentLabel}</>}
        </div>
        {unit && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            {(unit.number || special) && (
              <span style={{ fontFamily: FONT.heading, fontWeight: 700, fontSize: 14, color: accent, whiteSpace: 'nowrap' }}>{unit.number || special}</span>
            )}
            <h2 style={{
              margin: 0, minWidth: 0, fontFamily: FONT.heading, fontWeight: 700, fontSize: 17, lineHeight: 1.2, color: C.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              fontStyle: unit.kind === 'heading' ? 'italic' : 'normal',
            }}>{unit.title}</h2>
            {unit.page != null && <span style={{ fontFamily: FONT.ui, fontSize: 11.5, color: C.muted, whiteSpace: 'nowrap' }}>NCERT p. {unit.page}</span>}
          </div>
        )}
      </div>
      {unit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <PyqChip list={unit.pyq} />
          {ops && stickies.map((st, i) => <StickyChip key={st.id} note={st} n={i + 1} ops={ops} />)}
          {deck && onView && <ViewSwitch view={view} onChange={onView} count={slidesCount} />}
          <Pager compact prev={prev} next={next} onGo={onGo} labelOf={labelOf} />
          <span aria-hidden="true" style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }} />
        </div>
      )}
      {/* mousedown would clear the selected words before the click: keep them */}
      <button type="button" data-testid="v2-strip-highlight" onMouseDown={(e) => e.preventDefault()}
        onClick={onHighlight} title="Highlight — select words in the notes, then press this" aria-label="Highlight"
        style={{ ...stripBtn, background: '#FFF4B8' }}>
        <IconHighlight />
      </button>
      <button type="button" data-testid="v2-strip-sticky" onClick={onAddSticky}
        title="Add stick-on to this page" aria-label="Add stick-on" style={stripBtn}>
        <IconSticky />
      </button>
      {search && (open ? (
        <form onSubmit={submit} role="search" style={{ margin: 0 }}>
          <label style={{ ...stripBtn, width: 'auto', padding: '0 10px', gap: 8, cursor: 'text' }}>
            <IconSearch style={{ color: C.muted }} />
            <input ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)}
              onBlur={() => { if (!q.trim()) setOpen(false); }}
              placeholder="Search notes" aria-label="Search notes" style={{
                width: 160, border: 0, outline: 0, background: 'transparent',
                fontFamily: FONT.ui, fontSize: 13, color: C.text,
              }} />
          </label>
        </form>
      ) : (
        <button type="button" data-testid="v2-strip-search" onClick={() => setOpen(true)}
          title="Search notes" aria-label="Search notes" style={stripBtn}>
          <IconSearch style={{ color: C.muted }} />
        </button>
      ))}
      {pageCount > 0 && (
        <span data-testid="v2-page-count" title={`Page ${pageNo} of ${pageCount}`} style={{ fontFamily: FONT.ui, fontSize: 11.5, color: C.muted, marginLeft: 4, whiteSpace: 'nowrap' }}>
          {pageNo} / {pageCount}
        </span>
      )}
      {pageCount > 0 && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, background: '#EEF2FF' }}
          role="progressbar" aria-valuemin={1} aria-valuemax={pageCount} aria-valuenow={pageNo} aria-label="Pages read in this chapter">
          <div style={{ width: `${(pageNo / pageCount) * 100}%`, height: '100%', background: accent }} />
        </div>
      )}
    </div>
  );
}

function Outline({ units, depth, active, onGo, top = 0, seen }) {
  const ref = useRef(null);
  // keep the unit being read visible inside the outline column (without moving the page)
  useEffect(() => {
    const nav = ref.current;
    if (!nav || !active) return;
    const item = nav.querySelector(`[data-code="${CSS.escape(active)}"]`);
    if (!item) return;
    const top = item.getBoundingClientRect().top - nav.getBoundingClientRect().top + nav.scrollTop;
    if (top < nav.scrollTop + 60 || top + item.offsetHeight > nav.scrollTop + nav.clientHeight - 20) {
      nav.scrollTop = Math.max(0, top - nav.clientHeight / 3);
    }
  }, [active]);
  return (
    <nav ref={ref} aria-label="In this chapter" style={{
      flex: '1 1 240px', maxWidth: 290, boxSizing: 'border-box', alignSelf: 'flex-start',
      position: 'sticky', top: top + STRIP_H, maxHeight: `calc(100vh - ${top + STRIP_H}px)`, overflowY: 'auto',
      padding: '18px 12px', display: 'flex', flexDirection: 'column', gap: 2,
      borderRight: `1px solid ${C.border}`, fontSize: 14,
    }}>
      <div style={{ padding: '0 10px 2px', fontSize: 12, fontWeight: 600, color: C.muted }}>Pages in this chapter</div>
      <div style={{ padding: '0 10px 8px', fontSize: 12, color: C.muted }}>The small number is the NCERT page</div>
      {units.map((u) => {
        const on = u.code === active;
        const d = depth[u.code] || 0;
        return (
          <a key={u.code} href={`#${ID.unit(u.code)}`} className="v2-outline-item"
            data-testid="v2-outline-item" data-code={u.code} aria-current={on ? 'true' : undefined}
            onClick={(e) => { e.preventDefault(); onGo(u.code); }}
            style={{
              display: 'flex', alignItems: 'baseline', gap: 8,
              padding: `7px 10px 7px ${10 + 14 * d}px`, borderRadius: 6, textDecoration: 'none',
              background: on ? '#E0E7FF' : 'transparent', color: on ? C.primaryDark : C.text,
              fontWeight: on ? 600 : (d === 0 ? 500 : 400),
              fontStyle: u.kind === 'heading' ? 'italic' : 'normal',
            }}>
            <span style={{ flexGrow: 1 }}>
              {unitLabel(u)}
              {u.page != null && (
                <sup style={{ marginLeft: 4, fontSize: 11, fontStyle: 'normal', color: on ? C.primaryDark : C.muted }}>{u.page}</sup>
              )}
            </span>
            {(u.kind === 'added_topic' || u.kind === 'misc') && <ExtraPill />}
            {seen && seen.has(u.code) && !on && <span className="v2-outline-done" title="Read" style={{ alignSelf: 'center' }}><IconCheck /></span>}
          </a>
        );
      })}
      <div style={{ marginTop: 'auto', padding: '14px 10px 4px', borderTop: `1px solid ${C.border}` }}>
        <ColourKey light />
      </div>
    </nav>
  );
}

function Figure({ image }) {
  const [failed, setFailed] = useState(false);
  const [big, setBig] = useState(false);
  useEffect(() => {
    if (!big) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);
  const caption = image.caption ? (
    <figcaption style={{
      display: 'flex', gap: 10, alignItems: 'baseline', fontFamily: FONT.ui, fontSize: 14, lineHeight: 1.45, color: C.muted,
    }}>
      <span style={{
        flexShrink: 0, padding: '2px 9px', borderRadius: 999, background: C.tint, color: C.primaryDark,
        fontSize: 11.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
      }}>Figure</span>
      <span>{image.caption}</span>
    </figcaption>
  ) : null;
  return (
    <figure data-testid="v2-figure" style={{
      margin: '6px 0', display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: 16,
      background: '#FFFFFF', border: `1px solid ${C.border}`, boxShadow: '0 6px 20px rgba(15,23,42,.06)',
    }}>
      {!failed && (
        <button type="button" onClick={() => setBig(true)} aria-label="Make the picture bigger" title="Click to make it bigger" style={{
          padding: 0, border: 0, background: 'transparent', cursor: 'zoom-in', alignSelf: 'center', maxWidth: '100%',
        }}>
          <img src={image.url} alt={image.caption || 'NCERT figure'} onError={() => setFailed(true)}
            style={{ display: 'block', maxWidth: '100%', maxHeight: 560, height: 'auto', borderRadius: 8 }} />
        </button>
      )}
      {caption}
      {big && (
        <div role="dialog" aria-modal="true" aria-label={image.caption || 'Picture'} onClick={() => setBig(false)} style={{
          position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(15, 23, 42, 0.82)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20,
        }}>
          <img src={image.url} alt={image.caption || 'NCERT figure'} onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '94vw', maxHeight: '80vh', background: '#FFFFFF', borderRadius: 8 }} />
          {image.caption && <div style={{ maxWidth: 800, color: '#FFFFFF', fontFamily: FONT.ui, fontSize: 14, textAlign: 'center' }}>{image.caption}</div>}
          <button type="button" onClick={() => setBig(false)} style={{
            minHeight: 44, padding: '0 20px', border: 0, borderRadius: 10, background: '#FFFFFF',
            color: C.text, fontFamily: FONT.ui, fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}>Close</button>
        </div>
      )}
    </figure>
  );
}

function ReviewPill() {
  return (
    <span title="The words of this point changed. Click the dashed box to check your highlight." style={{
      display: 'inline-block', padding: '0 8px', border: '1px dashed #B45309', borderRadius: 10,
      fontFamily: FONT.ui, fontSize: 11, lineHeight: '18px', color: '#92400E', fontWeight: 500,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>Highlight changed</span>
  );
}

function PointText({ point, highlights }) {
  // html is sanitised by the server (services/v2_html.py: only b/i/sub/sup/br, colour spans
  // and tables survive); the highlights are wrapped in <mark> here (components/app/marks.js).
  const html = useMemo(() => decorateHtml(markHtml(point.html, highlights)), [point.html, highlights]);
  return (
    <div className="v2-pt" data-pt={point.code} style={{ flex: '1 1 auto', minWidth: 0 }}
      dangerouslySetInnerHTML={{ __html: html }} />
  );
}

function PointRow({ p, hls, qbox, top }) {
  const boxed = Boolean(qbox && qbox.kind === 'point' && qbox.code === p.code);
  const row = (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: top ? 'nowrap' : 'nowrap' }}>
      <PointText point={p} highlights={hls} />
      {hls && hls.some((h) => h.needs_review) && <ReviewPill />}
      {!top && p.source === 'extra' && <BeyondTag />}
    </div>
  );
  return boxed ? <AnswerBox label={qbox.label || boxLabel('point', qbox.number)}>{row}</AnswerBox> : row;
}

// Sub-points: a guide line on the left and small dots.
function SubList({ items, kids, imagesAfter, hlBy, qbox, answer }) {
  return (
    <ul style={{
      margin: '10px 0 0', padding: '2px 0 2px 18px', listStyle: 'none',
      borderLeft: `2px solid ${answer ? '#99F6E4' : '#C7D2FE'}`, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      {answer && (
        <li style={{ fontFamily: FONT.ui, fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#0F766E' }}>
          Answer
        </li>
      )}
      {items.map((p) => {
        const children = kids[p.code] || [];
        const figs = imagesAfter[p.code] || [];
        return (
          <React.Fragment key={p.code}>
            <li id={ID.point(p.code)} data-code={p.code} style={{ position: 'relative' }}>
              <span aria-hidden="true" style={{
                position: 'absolute', left: -25, top: '0.62em', width: 10, height: 10, borderRadius: 5,
                background: answer ? '#14B8A6' : C.primary, border: '2px solid #FFFFFF',
              }} />
              <PointRow p={p} hls={hlBy[p.code]} qbox={qbox} />
              {children.length > 0 && (
                <SubList items={children} kids={kids} imagesAfter={imagesAfter} hlBy={hlBy} qbox={qbox} />
              )}
            </li>
            {figs.length > 0 && <li style={{ listStyle: 'none' }}>{figs.map((im) => <Figure key={im.id} image={im} />)}</li>}
          </React.Fragment>
        );
      })}
    </ul>
  );
}

// Top-level points: one card each, numbered (questions "Q1…" on the Exercises page).
function PointList({ items, kids, imagesAfter, hlBy, qbox, kind, shown = Infinity }) {
  const ex = kind === 'exercises';
  const sum = kind === 'summary';
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.slice(0, shown).map((p, i) => {
        const children = kids[p.code] || [];
        const figs = imagesAfter[p.code] || [];
        const extra = p.source === 'extra';
        const exam = hasExamLine(p.html);
        const accent = extra ? '#10B981' : exam ? '#F59E0B' : ex ? '#14B8A6' : sum ? '#F59E0B' : C.primary;
        return (
          <React.Fragment key={p.code}>
            <li id={ID.point(p.code)} data-code={p.code} className="v2-card v2-reveal" style={{
              display: 'flex', gap: 14, padding: '14px 18px 14px 14px', borderRadius: 14,
              background: extra ? '#F0FDF4' : ex ? '#F0FDFA' : C.surface,
              border: extra ? '1px dashed #6EE7B7' : `1px solid ${C.border}`,
              borderLeft: `5px solid ${accent}`,
            }}>
              <span aria-hidden="true" style={{
                flexShrink: 0, minWidth: 30, height: 30, padding: '0 6px', boxSizing: 'border-box', borderRadius: 15,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
                background: extra ? '#D1FAE5' : ex ? '#CCFBF1' : C.tint,
                color: extra ? '#065F46' : ex ? '#0F766E' : C.primaryDark,
                fontFamily: FONT.ui, fontSize: 13, fontWeight: 700,
              }}>{sum ? <IconCheck /> : ex ? 'Q' : i + 1}</span>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                {(extra || exam) && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    {exam && <ExamTag />}
                    {extra && <BeyondTag />}
                  </div>
                )}
                <PointRow p={p} hls={hlBy[p.code]} qbox={qbox} top />
                {children.length > 0 && (
                  <SubList items={children} kids={kids} imagesAfter={imagesAfter} hlBy={hlBy} qbox={qbox} answer={ex} />
                )}
              </div>
            </li>
            {figs.length > 0 && <li className="v2-reveal" style={{ listStyle: 'none' }}>{figs.map((im) => <Figure key={im.id} image={im} />)}</li>}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

const yellowBox = {
  boxSizing: 'border-box', padding: '12px 14px', background: '#FFF4B8',
  border: '1px solid #E2CE6B', borderRadius: '4px 4px 14px 4px',
  flexDirection: 'column', gap: 8, fontFamily: FONT.notes, fontSize: 15, lineHeight: 1.45,
  color: C.text,
};
const noteLink = {
  minHeight: 32, padding: '0 8px', border: 0, background: 'transparent', color: '#5C4A00',
  fontFamily: FONT.ui, fontSize: 13, textDecoration: 'underline', cursor: 'pointer',
};
const smallBtn = {
  minHeight: 44, padding: '0 16px', borderRadius: 8, fontFamily: FONT.ui, fontSize: 14,
  fontWeight: 600, cursor: 'pointer',
};

// Write / change the words of a stick-on.
function StickyEditor({ initial, onSave, onCancel, label }) {
  const [body, setBody] = useState(initial || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  const left = STICKY_MAX - body.length;
  const save = async () => {
    if (!body.trim()) { setErr('Write something first.'); return; }
    setBusy(true);
    setErr('');
    try {
      await onSave(body.trim());
    } catch (e) {
      setBusy(false);
      setErr('Could not save. Please try again.');
    }
  };
  return (
    <div data-testid="v2-sticky-editor" style={{ ...yellowBox, display: 'flex', width: 360, maxWidth: '100%' }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}>
      <div style={{ fontFamily: FONT.ui, fontSize: 12, fontWeight: 600, color: '#5C4A00' }}>{label}</div>
      <textarea ref={ref} value={body} maxLength={STICKY_MAX} rows={4}
        aria-label="Write your stick-on here" placeholder="Write your note here"
        onChange={(e) => setBody(e.target.value.slice(0, STICKY_MAX))}
        style={{
          width: '100%', boxSizing: 'border-box', resize: 'vertical', padding: 8,
          border: '1px solid #E2CE6B', borderRadius: 6, background: '#FFFBE0',
          fontFamily: FONT.notes, fontSize: 15, lineHeight: 1.45, color: C.text,
        }} />
      <div style={{ fontFamily: FONT.ui, fontSize: 12, color: left < 30 ? '#92400E' : '#5C4A00' }}>
        {body.length} of {STICKY_MAX} letters
      </div>
      {err && <div role="alert" style={{ fontFamily: FONT.ui, fontSize: 13, color: C.bad }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={save} disabled={busy}
          style={{ ...smallBtn, border: 0, background: C.primary, color: '#FFFFFF', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ ...smallBtn, border: '1px solid #E2CE6B', background: '#FFFFFF', color: C.text, fontWeight: 500 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// One saved stick-on: a small button beside the unit title; the yellow note opens on
// hover, on keyboard focus, or with a click (a click keeps it open).
function StickyChip({ note, n, ops }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('view');   // view | edit | remove
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setMode('view'); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const pinned = open || mode !== 'view';
  return (
    <span ref={wrap} className={`v2-sticky-wrap${pinned ? ' open' : ''}`} data-testid="v2-sticky"
      style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" aria-expanded={pinned} onClick={() => setOpen((v) => !v)}
        aria-label={`My stick-on ${n}: ${note.body.slice(0, 40)}`} style={{
          display: 'flex', alignItems: 'center', gap: 6, height: 40, padding: '0 12px',
          border: '1px solid #E2CE6B', borderRadius: 20, background: '#FFF4B8', color: '#5C4A00',
          fontFamily: FONT.ui, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
        <IconSticky />My stick-on{n > 1 ? ` ${n}` : ''}
      </button>
      <div className="v2-sticky-pop" role="note" style={{
        position: 'absolute', top: '100%', right: 0, zIndex: 6, paddingTop: 6,
      }}>
        {mode === 'edit' ? (
          <StickyEditor label="Change my stick-on" initial={note.body}
            onCancel={() => setMode('view')}
            onSave={async (body) => { await ops.edit(note, body); setMode('view'); setOpen(false); }} />
        ) : (
          <div data-testid="v2-sticky-note" style={{
            ...yellowBox, display: 'flex', width: 340, maxWidth: '80vw',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.14)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT.ui, fontSize: 12, fontWeight: 600, color: '#5C4A00' }}>
              <div style={{ flexGrow: 1 }}>My stick-on</div>
              {mode === 'view' && (
                <>
                  <button type="button" style={noteLink} onClick={() => { setMode('edit'); setOpen(true); }}>Edit</button>
                  <button type="button" style={noteLink} onClick={() => { setMode('remove'); setOpen(true); }}>Remove</button>
                </>
              )}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note.body}</div>
            {mode === 'remove' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontFamily: FONT.ui, fontSize: 14 }}>
                <span style={{ flexBasis: '100%' }}>Remove this stick-on?</span>
                <button type="button" onClick={async () => {
                  try { await ops.remove(note); } catch (e) { setMode('view'); }
                }} style={{ ...smallBtn, border: 0, background: C.bad, color: '#FFFFFF' }}>Yes, remove</button>
                <button type="button" onClick={() => setMode('view')}
                  style={{ ...smallBtn, border: '1px solid #E2CE6B', background: '#FFFFFF', color: C.text, fontWeight: 500 }}>No</button>
              </div>
            )}
          </div>
        )}
      </div>
    </span>
  );
}

// "PYQ ×N" beside a unit title (C11.4): how many past-paper questions point to this unit.
// A click opens the list of their exams and years ("NEET 2019 · Q 12345").
function PyqChip({ list }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!list || !list.length) return null;
  return (
    <span ref={wrap} data-testid="v2-pyq-chip" style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        title="Past NEET questions on this part" style={{
          display: 'flex', alignItems: 'center', height: 40, padding: '0 12px',
          border: '1px solid #F1D3A1', borderRadius: 20, background: C.streakBg, color: C.streak,
          fontFamily: FONT.ui, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
        PYQ ×{list.length}
      </button>
      {open && (
        <div role="note" data-testid="v2-pyq-list" style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 6, marginTop: 6, width: 280,
          maxWidth: '80vw', maxHeight: 300, overflowY: 'auto', boxSizing: 'border-box',
          padding: '10px 14px', background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, boxShadow: '0 6px 20px rgba(15, 23, 42, 0.14)',
          fontFamily: FONT.ui, fontSize: 14, color: C.text,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
            Came in these exams
          </div>
          {list.map((x) => (
            <div key={x.question_id} style={{ display: 'flex', gap: 10, padding: '4px 0', borderTop: `1px solid ${C.border}` }}>
              <span style={{ flexGrow: 1, fontWeight: 600, color: C.streak }}>{x.label}</span>
              <span style={{ fontSize: 12, color: '#64748B', userSelect: 'text', whiteSpace: 'nowrap' }}>Q {x.question_id}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

// Bar under the items while they are still appearing one by one.
// Owner 2026-10-31: the reveal controls are three stacked icons in the bottom-right corner
// (Play/Pause, Next line, Show all) with the count above them, so nothing sits in the
// reading frame. Tooltips and small captions carry the words; Space still shows the next line.
const IconPlay = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 5v14l12-7z" /></svg>
);
const IconPause = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
);
const IconNextLine = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v12M6 11l6 6 6-6" /></svg>
);
const IconShowAll = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v16M5 9l7 7 7-7" /><path d="M5 20h14" /></svg>
);
function RevealBar({ shown, total, paused, onPause, onNext, onAll }) {
  const btn = (testid, title, icon, onClick, primary = false) => (
    <button type="button" data-testid={testid} onClick={onClick} title={title} aria-label={title} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, borderRadius: 10, border: 0, padding: 0,
      background: primary ? '#FFFFFF' : 'rgba(255,255,255,.12)', color: primary ? C.primaryDark : '#FFFFFF', cursor: 'pointer',
    }}>
      {icon}
    </button>
  );
  return (
    <div data-testid="v2-reveal-bar" style={{
      position: 'fixed', right: 10, bottom: 14, zIndex: 6, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 4, padding: '6px 5px', borderRadius: 14, background: 'rgba(30,27,75,.86)', color: '#FFFFFF',
      boxShadow: '0 6px 18px rgba(30,27,75,.3)',
    }}>
      <span style={{ fontFamily: FONT.ui, fontSize: 10, fontWeight: 700, lineHeight: 1, opacity: .85 }} aria-live="polite">{shown}/{total}</span>
      {btn('v2-reveal-pause', paused ? 'Play — lines keep appearing by themselves' : 'Pause — stop the lines appearing', paused ? <IconPlay /> : <IconPause />, onPause)}
      {btn('v2-reveal-next', 'Next line (Space key)', <IconNextLine />, onNext, true)}
      {btn('v2-reveal-all', 'Show all the lines of this page', <IconShowAll />, onAll)}
    </div>
  );
}

// One page = one NCERT unit (topic, subtopic, opening, Summary or Exercises).
// `deck` = the chapter's slides (components/app/SlideDeck.js) or null; `view` = 'theory' |
// 'slides' (owner 2026-10-31: the same page shows either the notes or that page's slides).
function NotePage({
  unit, parentLabel, chapterTitle, pageNo, pageCount, prev, next, onGo, onFinish,
  stickies, hlBy, adding, ops, onPlay, qbox, qmodeEnd, onJump, revealAll = false,
  deck = null, view = 'theory', onView, slideAt = null, onPresent,
}) {
  const pageSlides = slidesOf(deck, unit.code);
  const slidesOn = view === 'slides' && pageSlides.length > 0;
  const { tops, kids, imagesAfter, imagesAtStart } = useMemo(() => {
    const k = {};
    const t = [];
    unit.points.forEach((p) => {
      if (p.parent) (k[p.parent] = k[p.parent] || []).push(p);
      else t.push(p);
    });
    const after = {};
    const start = [];
    unit.images.forEach((im) => {
      if (im.after) (after[im.after] = after[im.after] || []).push(im);
      else start.push(im);
    });
    return { tops: t, kids: k, imagesAfter: after, imagesAtStart: start };
  }, [unit]);
  const recap = useMemo(() => recapOf(unit.points), [unit.points]);

  // ---- one item at a time (owner 2026-10-09): each top point (with its sub-points and
  // figures) appears after the previous one had time to be read; the time depends on how
  // much there is to read. Pause / Next / Show all; Space shows the next one at once.
  const total = tops.length;
  const [shown, setShown] = useState(revealAll ? total : Math.min(1, total));
  const [paused, setPaused] = useState(false);
  const done = shown >= total || slidesOn;
  const readMs = useCallback((p) => {
    const all = [p, ...(kids[p.code] || [])];
    const chars = all.reduce((n, x) => n + (x.plain_text || plainShort(x.html, 100000)).length, 0);
    const figs = (imagesAfter[p.code] || []).length;
    return Math.min(14000, Math.max(2200, 1200 + chars * 50 + figs * 3000));
  }, [kids, imagesAfter]);
  useEffect(() => {
    if (done || paused) return undefined;
    const t = setTimeout(() => setShown((n) => n + 1), readMs(tops[shown - 1] || tops[0]));
    return () => clearTimeout(t);
  }, [shown, done, paused, tops, readMs]);
  // keep the newest item in view
  const listRef = useRef(null);
  useEffect(() => {
    if (shown <= 1 || revealAll) return;
    const box = listRef.current;
    const items = box ? box.querySelectorAll('.v2-reveal') : [];
    const last = items[items.length - 1];
    if (!last) return;
    const r = last.getBoundingClientRect();
    if (r.bottom > window.innerHeight - 90) {
      window.scrollBy({ top: Math.min(r.bottom - window.innerHeight + 120, r.top - 160), behavior: 'smooth' });
    }
  }, [shown, revealAll]);
  const showNext = useCallback(() => setShown((n) => Math.min(total, n + 1)), [total]);
  const showAll = useCallback(() => setShown(total), [total]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== ' ' || done) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.isContentEditable)) return;
      e.preventDefault();
      showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [done, showNext]);
  const jump = useCallback((code) => { setShown(total); setTimeout(() => onJump(code), 30); }, [total, onJump]);
  const label = unitLabel(unit);
  const boxed = Boolean(qbox && qbox.kind === 'unit' && qbox.code === unit.code);
  const labelOf = (u) => unitLabel(u);

  // The page title and actions live in the top strip now (owner 2026-10-31). Notes opened from
  // a question keep a small boxed title line so "Read this part for Question N" has a place.
  const hero = boxed ? (
    <AnswerBox label={qbox.label || boxLabel('unit', qbox.number)}>
      <div style={{ fontFamily: FONT.heading, fontWeight: 700, fontSize: 20, color: C.text }}>{label}</div>
    </AnswerBox>
  ) : null;

  return (
    <section id={ID.unit(unit.code)} data-code={unit.code} data-testid="v2-unit" className="v2-page"
      key={unit.code} style={{ display: 'flex', flexDirection: 'column', gap: 18, scrollMarginTop: STRIP_H + 12 }}>
      {hero}
      {adding && (
        <StickyEditor label={`New stick-on · ${label}`} onCancel={ops.cancelAdd}
          onSave={(body) => ops.add(unit.code, body)} />
      )}
      <VideoLines clips={unit.clips} onPlay={(clip) => onPlay(clip, unit)} />
      {slidesOn && <SlideViewer slides={pageSlides} startId={slideAt} onPresent={onPresent} />}
      {!slidesOn && imagesAtStart.map((im) => <Figure key={im.id} image={im} />)}
      <div ref={listRef} style={slidesOn ? { display: 'none' } : undefined}>
        {tops.length > 0
          ? <PointList items={tops} kids={kids} imagesAfter={imagesAfter} hlBy={hlBy} qbox={qbox} kind={unit.kind} shown={shown} />
          : unit.kind === 'misc'
            // C12.2: the chapter's Misc / Latest / Integrated page may be empty for now
            ? (!(unit.clips || []).length && !unit.images.length && (
              <div data-testid="v2-misc-empty" style={{ color: C.muted, fontFamily: FONT.ui, fontSize: 16, lineHeight: 1.5 }}>
                Nothing extra here yet. New things for this chapter will appear here.
              </div>))
            : <div style={{ color: C.muted, fontFamily: FONT.ui }}>No points on this page yet.</div>}
      </div>
      {!done && (
        <RevealBar shown={shown} total={total} paused={paused} onPause={() => setPaused((v) => !v)}
          onNext={showNext} onAll={showAll} />
      )}
      {done && !slidesOn && <RecapCard recap={recap} onJump={jump} />}
      {done && qmodeEnd && <EndBackButton qmode={qmodeEnd} />}
      {done && <Pager prev={prev} next={next} onGo={onGo} labelOf={labelOf} pageNo={pageNo} pageCount={pageCount} onFinish={onFinish} />}
    </section>
  );
}

// Small "Highlight" button just above the selected words.
function SelectionButton({ pos, onHighlight }) {
  if (!pos) return null;
  return (
    <button type="button" data-testid="v2-float-highlight" onMouseDown={(e) => e.preventDefault()}
      onClick={onHighlight} style={{
        position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)', zIndex: 20,
        display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 14px',
        border: '1px solid #CBD5E1', borderRadius: 22, background: '#FFF4B8', color: C.text,
        fontFamily: FONT.ui, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
      }}>
      <IconHighlight />Highlight
    </button>
  );
}

// The word or short term to look up for a selection, or null ("DNA," → "DNA").
const WORD_EDGE = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
function wordOf(selText) {
  const t = (selText || '').replace(/\s+/g, ' ').trim().replace(WORD_EDGE, '');
  if (!t || t.length > 40 || !/\p{L}/u.test(t)) return null;
  const n = t.split(' ').length;
  return n <= 3 ? { text: t, live: n === 1 } : null;
}

// Double-click on a hyphenated word ("N-glycosidic", "semi-conservative") selects the whole
// word, not only the part between hyphens (C10.1). Works on one text node's string:
// returns the widened [start, end) (trailing spaces that Windows adds are dropped).
const WORD_CH = /[\p{L}\p{N}]/u;
const HYPHENS = '-\u2010\u2011';
export function widenOverHyphens(text, start, end) {
  let s = start;
  let e = end;
  while (e > s && /\s/.test(text[e - 1])) e -= 1;
  while (s >= 2 && HYPHENS.includes(text[s - 1]) && WORD_CH.test(text[s - 2])) {
    s -= 1;
    while (s > 0 && WORD_CH.test(text[s - 1])) s -= 1;
  }
  while (e + 1 < text.length && HYPHENS.includes(text[e]) && WORD_CH.test(text[e + 1])) {
    e += 1;
    while (e < text.length && WORD_CH.test(text[e])) e += 1;
  }
  return [s, e];
}

// reasons for which the box still opens (to say why there is no meaning); not cached
const BOX_REASONS = ['limit', 'unavailable'];
const NOT_FOUND_TEXT = {
  limit: 'You have looked up many new words in the last hour. Please try again later.',
  unavailable: 'The meaning could not be found right now. Please try again later.',
};

// "Word meaning" box under (or above) the selected word, with the Highlight button.
function WordPopover({ pos, info, onHighlight }) {
  const W = 380;
  const below = pos.rBottom + 8 + 230 < window.innerHeight;
  const left = Math.min(Math.max(pos.left - W / 2, 8), window.innerWidth - W - 8);
  const d = info.data;
  const label = { fontFamily: FONT.ui, fontSize: 12, fontWeight: 600, color: C.muted };
  return (
    <div role="dialog" aria-label={`Word meaning: ${info.text}`} data-testid="v2-word-pop"
      onMouseDown={(e) => e.preventDefault()} style={{
        position: 'fixed', zIndex: 20, left, width: W, maxWidth: 'calc(100vw - 16px)',
        ...(below ? { top: pos.rBottom + 8 } : { top: pos.rTop - 8, transform: 'translateY(-100%)' }),
        boxSizing: 'border-box', padding: '14px 16px', background: C.surface, border: '1px solid #CBD5E1',
        borderRadius: 10, boxShadow: '0 6px 20px rgba(15, 23, 42, 0.14)', display: 'flex',
        flexDirection: 'column', gap: 8, fontFamily: FONT.notes, fontSize: 16, lineHeight: 1.45, color: C.text,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flexGrow: 1, minWidth: 0, fontFamily: FONT.ui, fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {info.text}
        </div>
        <div style={{ fontFamily: FONT.ui, fontSize: 12, color: C.muted }}>Word meaning</div>
        <button type="button" data-testid="v2-word-highlight" onClick={onHighlight} style={{
          display: 'flex', alignItems: 'center', gap: 6, height: 44, padding: '0 12px', flexShrink: 0,
          border: '1px solid #CBD5E1', borderRadius: 22, background: '#FFF4B8', color: C.text,
          fontFamily: FONT.ui, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}><IconHighlight />Highlight</button>
      </div>
      {info.status === 'loading' && <div style={{ fontFamily: FONT.ui, fontSize: 14, color: C.muted }}>Finding the meaning…</div>}
      {info.status === 'done' && d && d.found && (
        <>
          <div data-testid="v2-word-en"><span style={label}>English</span><br />{d.meaning_en}</div>
          {d.meaning_hi && (
            <div data-testid="v2-word-hi" lang="hi" style={{ fontFamily: FONT.hindi }}>
              <span style={label}>Hindi</span><br />{d.meaning_hi}
            </div>
          )}
        </>
      )}
      {info.status === 'done' && d && !d.found && (
        <div style={{ fontFamily: FONT.ui, fontSize: 14, color: C.muted }}>
          {NOT_FOUND_TEXT[d.reason] || 'No meaning found for this word.'}
        </div>
      )}
      {info.status === 'error' && (
        <div style={{ fontFamily: FONT.ui, fontSize: 14, color: C.bad }}>Could not look up this word. Please try again.</div>
      )}
    </div>
  );
}

// Menu that opens when a highlight is clicked.
function HighlightMenu({ menu, onRemove, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose);
    };
  }, [menu, onClose]);
  if (!menu) return null;
  const { hl } = menu;
  return (
    <div ref={ref} role="dialog" aria-label="Highlight" data-testid="v2-hl-menu" style={{
      position: 'fixed', top: menu.top, left: menu.left, zIndex: 20, maxWidth: 340,
      boxSizing: 'border-box', padding: 12, background: C.surface, border: '1px solid #CBD5E1',
      borderRadius: 10, boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
      display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT.ui, fontSize: 14, color: C.text,
    }}>
      {hl.needs_review && (
        <div style={{ lineHeight: 1.45 }}>
          The words of this point changed. You had highlighted:
          <div style={{ marginTop: 4, fontFamily: FONT.notes, fontSize: 15, background: C.marker, padding: '2px 6px', borderRadius: 4 }}>
            “{hl.quoted_text}”
          </div>
          Remove it and highlight the new words again if you want.
        </div>
      )}
      <button type="button" onClick={() => onRemove(hl)} autoFocus style={{
        ...smallBtn, border: '1px solid #CBD5E1', background: C.surface, color: C.text,
      }}>Remove highlight</button>
    </div>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div role="status" data-testid="v2-notes-toast" style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30,
      maxWidth: '90vw', padding: '12px 18px', borderRadius: 10, background: C.bar, color: '#FFFFFF',
      fontFamily: FONT.ui, fontSize: 15, boxShadow: '0 6px 20px rgba(15, 23, 42, 0.25)',
    }}>{msg}</div>
  );
}

// ------------------------------------------------------------------ page

function readLast(chapterId) {
  try { return localStorage.getItem(LAST_UNIT_KEY(chapterId)); } catch { return null; }
}
function saveLast(chapterId, code) {
  try { localStorage.setItem(LAST_UNIT_KEY(chapterId), code); } catch { /* private mode */ }
}

function scrollToEl(el, flash, offset = STRIP_H) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - offset - 12;
  window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  if (flash) {
    el.classList.remove('v2-flash');
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth; // restart the animation
    el.classList.add('v2-flash');
    setTimeout(() => el.classList.remove('v2-flash'), 2800);
  }
}

// notes_open / notes_read for the unit being read (only time while the page is visible).
function useReadTracking(chapterId, unitCode, remember = true) {
  useEffect(() => {
    if (!unitCode) return undefined;
    const cid = Number(chapterId);
    let acc = 0;
    let since = document.visibilityState === 'hidden' ? null : Date.now();
    track('notes_open', { chapter_id: cid, section_code: unitCode });
    if (remember) saveLast(chapterId, unitCode);
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (since != null) { acc += Date.now() - since; since = null; }
      } else if (since == null) {
        since = Date.now();
      }
    };
    const send = () => {
      const ms = acc + (since != null ? Date.now() - since : 0);
      acc = 0;
      since = null;
      if (ms >= 1000) track('notes_read', { chapter_id: cid, section_code: unitCode, duration_ms: Math.round(ms) });
    };
    // Window closed / reloaded: React does not unmount, so send it here (the activity
    // client keeps it in localStorage and sends it at the latest on the next start).
    const onHide = () => send();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
      send();
    };
  }, [chapterId, unitCode, remember]);
}

export default function AppNotes() {
  const { chapterId } = useParams();
  const [params] = useSearchParams();
  const at = params.get('at') || params.get('unit');
  return <NotesScreen key={chapterId} chapterId={chapterId} at={at} />;
}

// The Notes screen itself. `qmode` (C7.5) = opened from a question; see the top of this file.
export function NotesScreen({ chapterId, at, qmode = null }) {
  useActivityArea('notes');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['v2-notes', chapterId],
    queryFn: () => getChapterNotes(chapterId),
    retry: (n, err) => !(err && err.response && err.response.status === 404) && n < 2,
  });

  const units = useMemo(() => (data ? data.units : []), [data]);
  const depth = useMemo(() => {
    const parent = {};
    units.forEach((u) => { parent[u.code] = u.parent; });
    const d = {};
    units.forEach((u) => {
      let n = 0;
      let p = parent[u.code];
      while (p && n < 6) { n += 1; p = parent[p]; }
      d[u.code] = n;
    });
    return d;
  }, [units]);
  const stickiesBy = useMemo(() => {
    const m = {};
    ((data && data.stickies) || []).forEach((s) => { (m[s.unit_code] = m[s.unit_code] || []).push(s); });
    return m;
  }, [data]);
  const hlBy = useMemo(() => {
    const m = {};
    ((data && data.highlights) || []).forEach((h) => { (m[h.point_code] = m[h.point_code] || []).push(h); });
    return m;
  }, [data]);
  const unitOf = useMemo(() => {
    const m = {};
    units.forEach((u) => u.points.forEach((p) => { m[p.code] = u.code; }));
    return m;
  }, [units]);

  // ---- qmode (C7.5): the boxed place and the unit after which "I have read it" comes
  const qNumber = qmode ? qmode.number : null;
  const qbox = useMemo(() => {
    if (!qmode || !at) return null;
    const text = qmode.boxText || null;
    if (unitOf[at]) return { kind: 'point', code: at, unit: unitOf[at], number: qNumber, label: text && text('point') };
    if (units.some((u) => u.code === at)) return { kind: 'unit', code: at, unit: at, number: qNumber, label: text && text('unit') };
    return null;
  }, [qmode, at, unitOf, units, qNumber]);

  // ---- marks (C5.1): the page's copy of the notes is updated in place after each save
  const queryClient = useQueryClient();
  const cid = Number(chapterId);
  const patch = useCallback((fn) => {
    queryClient.setQueryData(['v2-notes', chapterId], (old) => (old ? fn(old) : old));
  }, [queryClient, chapterId]);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);
  const say = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const failSay = useCallback((e) => {
    const st = e && e.response && e.response.status;
    say(st === 409 ? 'The notes have changed. Please reload the page.' : 'Could not save. Please try again.');
  }, [say]);

  const bodyRef = useRef(null);
  // Height of what is pinned at the top: the strip, or (qmode) the Back bar + the strip.
  // On the normal page (not qmode: that page is full screen, no menu, no title bar) the top
  // menu (C11.1) and, in the desktop app, the 44 px title bar are pinned above the strip, so
  // scrolls and the scroll-spy allow for them too (pinnedTop()).
  const barH = qmode ? 0 : pinnedTop();
  const headRef = useRef(null);
  const headH = useRef(barH + STRIP_H);
  const hasQmode = Boolean(qmode);
  const hasData = Boolean(data);
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return undefined;
    const measure = () => { headH.current = barH + (el.offsetHeight || STRIP_H); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasQmode, hasData, barH]);
  const [selPos, setSelPos] = useState(null);
  const [hlMenu, setHlMenu] = useState(null);
  const closeHlMenu = useCallback(() => setHlMenu(null), []);

  // ---- dictionary (C5.2): look up a selected word after the selection settles
  const selWord = useMemo(() => (selPos ? wordOf(selPos.text) : null), [selPos]);
  const selKey = selWord ? `${selWord.live ? 1 : 0}:${selWord.text.toLowerCase()}` : '';
  const [wordInfo, setWordInfo] = useState(null);   // {key, text, status, data}
  const dictCache = useRef(new Map());

  // the selected words, split into one piece per point
  const selectedPieces = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !bodyRef.current) return [];
    const range = sel.getRangeAt(0);
    if (!range.intersectsNode(bodyRef.current)) return [];
    const out = [];
    bodyRef.current.querySelectorAll('[data-pt]').forEach((el) => {
      if (!range.intersectsNode(el)) return;
      const o = offsetsInPoint(range, el);
      if (o) out.push({ code: el.getAttribute('data-pt'), ...o });
    });
    return out;
  }, []);

  // double-click over a hyphenated word → select the whole word (then the word box opens)
  useEffect(() => {
    const onDbl = (ev) => {
      if (!bodyRef.current || !bodyRef.current.contains(ev.target)) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const r = sel.getRangeAt(0);
      if (r.startContainer !== r.endContainer || r.startContainer.nodeType !== Node.TEXT_NODE) return;
      const node = r.startContainer;
      const [s0, e0] = widenOverHyphens(node.data, r.startOffset, r.endOffset);
      if (s0 !== r.startOffset || e0 !== r.endOffset) sel.setBaseAndExtent(node, s0, node, e0);
    };
    document.addEventListener('dblclick', onDbl);
    return () => document.removeEventListener('dblclick', onDbl);
  }, []);

  // show the small Highlight button above a selection inside the notes
  useEffect(() => {
    let raf = null;
    const place = () => {
      raf = null;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !bodyRef.current) { setSelPos(null); return; }
      const range = sel.getRangeAt(0);
      const inside = range.intersectsNode(bodyRef.current)
        && Array.from(bodyRef.current.querySelectorAll('[data-pt]')).some((el) => range.intersectsNode(el));
      if (!inside) { setSelPos(null); return; }
      const r = range.getBoundingClientRect();
      const top = r.top - 52 < headH.current + 4 ? r.bottom + 8 : r.top - 52;
      setSelPos({
        top, left: Math.min(Math.max(r.left + r.width / 2, 80), window.innerWidth - 80),
        rTop: r.top, rBottom: r.bottom, text: sel.toString(),
      });
    };
    const later = () => { if (!raf) raf = requestAnimationFrame(place); };
    document.addEventListener('selectionchange', later);
    window.addEventListener('scroll', later, { passive: true });
    return () => {
      document.removeEventListener('selectionchange', later);
      window.removeEventListener('scroll', later);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // a dict_lookup event each time the Word meaning box shows a result
  const reportLookup = useCallback((wtext, data) => {
    if (!data || !(data.found || BOX_REASONS.includes(data.reason))) return;
    const pc = selectedPieces()[0];
    track('dict_lookup', {
      chapter_id: cid, section_code: pc ? unitOf[pc.code] : undefined, point_code: pc ? pc.code : undefined,
      details: { word: wtext, found: Boolean(data.found), source: data.source || null, reason: data.reason || null },
    });
  }, [selectedPieces, cid, unitOf]);

  useEffect(() => {
    if (!selWord) { setWordInfo(null); return undefined; }
    const { text: wtext, live } = selWord;
    const key = selKey;
    const cached = dictCache.current.get(key);
    let gone = false;
    if (cached) {
      setWordInfo({ key, text: wtext, status: 'done', data: cached });
      const t = setTimeout(() => { if (!gone) reportLookup(wtext, cached); }, 350);
      return () => { gone = true; clearTimeout(t); };
    }
    setWordInfo({ key, text: wtext, status: 'loading', data: null });
    const timer = setTimeout(async () => {
      try {
        const data = await lookupWord(wtext, cid, { live });
        if (!BOX_REASONS.includes(data.reason)) dictCache.current.set(key, data);
        if (gone) return;
        setWordInfo({ key, text: wtext, status: 'done', data });
        reportLookup(wtext, data);
      } catch (e) {
        if (!gone) setWordInfo({ key, text: wtext, status: 'error', data: null });
      }
    }, 350);
    return () => { gone = true; clearTimeout(timer); };
  }, [selKey]);
  // show the box only for a looked-up single word, or for a short term that was found
  const showWord = Boolean(selPos && wordInfo && wordInfo.key === selKey && (
    (wordInfo.status === 'done' && wordInfo.data && (wordInfo.data.found
      || BOX_REASONS.includes(wordInfo.data.reason)))
    || wordInfo.status === 'error'));

  const busyHl = useRef(false);
  const doHighlight = useCallback(async () => {
    if (busyHl.current) return;
    const pieces = selectedPieces();
    if (pieces.length === 0) {
      say('Select some words in the notes first, then press Highlight.');
      return;
    }
    busyHl.current = true;
    let failed = null;
    for (const pc of pieces) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await addHighlight({
          point_code: pc.code, start_offset: pc.start, end_offset: pc.end,
          quoted_text: pc.text, colour: 'yellow',
        });
        const gone = new Set(res.removed || []);
        patch((old) => ({
          ...old,
          highlights: [
            ...old.highlights.filter((h) => h.id !== res.highlight.id && !gone.has(h.id)),
            res.highlight,
          ],
        }));
        track('highlight_add', {
          chapter_id: cid, section_code: unitOf[pc.code], point_code: pc.code,
          details: { id: res.highlight.id, chars: res.highlight.end - res.highlight.start, merged: Boolean(res.merged) },
        });
      } catch (e) {
        failed = e;
      }
    }
    busyHl.current = false;
    const sel = window.getSelection();
    if (sel) sel.removeAllRanges();
    setSelPos(null);
    if (failed) failSay(failed);
  }, [selectedPieces, say, patch, cid, unitOf, failSay]);

  const doRemoveHighlight = useCallback(async (hl) => {
    setHlMenu(null);
    try {
      await removeHighlight(hl.id);
      patch((old) => ({ ...old, highlights: old.highlights.filter((h) => h.id !== hl.id) }));
      track('highlight_remove', {
        chapter_id: cid, section_code: unitOf[hl.point_code], point_code: hl.point_code, details: { id: hl.id },
      });
    } catch (e) {
      failSay(e);
    }
  }, [patch, cid, unitOf, failSay]);

  // click on a highlight → its menu (not while words are being selected)
  const onBodyClick = useCallback((e) => {
    const m = e.target.closest && e.target.closest('mark[data-hl]');
    if (!m) return;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    const id = Number(m.getAttribute('data-hl'));
    const hl = ((data && data.highlights) || []).find((h) => h.id === id);
    if (!hl) return;
    const r = m.getBoundingClientRect();
    setHlMenu({ hl, top: Math.min(r.bottom + 6, window.innerHeight - 160), left: Math.min(Math.max(r.left, 8), window.innerWidth - 350) });
  }, [data]);

  // stick-ons
  const [addingFor, setAddingFor] = useState(null);
  // Video clip being played over the notes (C6.1)
  const [video, setVideo] = useState(null);
  const playClip = useCallback((clip, unit) => {
    const sel = window.getSelection && window.getSelection();
    if (sel) sel.removeAllRanges();
    setVideo({ clip, unit });
  }, []);
  const closeVideo = useCallback(() => setVideo(null), []);
  const stickyOps = useMemo(() => ({
    startAdd: (code) => setAddingFor(code),
    cancelAdd: () => setAddingFor(null),
    add: async (code, body) => {
      try {
        const st = await addSticky(code, body);
        patch((old) => ({ ...old, stickies: [...old.stickies, st] }));
        setAddingFor(null);
        track('sticky_add', { chapter_id: cid, section_code: code, details: { id: st.id, chars: st.body.length } });
      } catch (e) { failSay(e); throw e; }
    },
    edit: async (note, body) => {
      try {
        const st = await editSticky(note.id, body);
        patch((old) => ({ ...old, stickies: old.stickies.map((x) => (x.id === st.id ? st : x)) }));
        track('sticky_edit', { chapter_id: cid, section_code: note.unit_code, details: { id: st.id, chars: st.body.length } });
      } catch (e) { failSay(e); throw e; }
    },
    remove: async (note) => {
      try {
        await removeSticky(note.id);
        patch((old) => ({ ...old, stickies: old.stickies.filter((x) => x.id !== note.id) }));
        track('sticky_remove', { chapter_id: cid, section_code: note.unit_code, details: { id: note.id } });
      } catch (e) { failSay(e); throw e; }
    },
  }), [patch, cid, failSay]);

  // ---- pages (owner 2026-10-09): one unit per page, Previous / Next
  const navigate = useNavigate();
  const pageOfAt = useMemo(() => {
    if (!at) return null;
    if (unitOf[at]) return unitOf[at];
    return units.some((u) => u.code === at) ? at : null;
  }, [at, unitOf, units]);
  const [active, setActive] = useState(null);     // the unit on the page
  const [reading, setReading] = useState(null);   // the unit counted as opened (after 1 s)
  const [revealAll, setRevealAll] = useState(false);
  useReadTracking(chapterId, reading, !qmode);

  // Theory | Slides (owner 2026-10-31). The chapter's slides file, if any; not when the
  // notes were opened from a question (the boxed answer is in the theory).
  const { data: deck } = useChapterSlides(!qmode && data ? chapterCode(data.chapter) : null);
  const [view, setView] = useState(readView);
  const changeView = useCallback((v) => { setView(v); saveView(v); }, []);
  // Full-screen slides (owner 2026-11-02): runs through the whole chapter; on leaving, the
  // notes page opens on the page of the slide he was on, at that slide.
  const [presentAt, setPresentAt] = useState(null);   // slide id while full screen is open
  const [slideAt, setSlideAt] = useState(null);       // the slide the page's viewer should show

  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setReading(active), COMMIT_MS);
    return () => clearTimeout(t);
  }, [active]);

  // pages seen on this laptop (a tick in the outline)
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY(chapterId)) || '[]')); } catch { return new Set(); }
  });
  useEffect(() => {
    if (!reading || qmode) return;
    setSeen((old) => {
      if (old.has(reading)) return old;
      const n = new Set(old);
      n.add(reading);
      try { localStorage.setItem(SEEN_KEY(chapterId), JSON.stringify([...n])); } catch { /* private mode */ }
      return n;
    });
  }, [reading, qmode, chapterId]);

  // First view: the deep link (?at= / ?unit=), else the unit read last here, else page 1.
  const placed = useRef(null);
  useLayoutEffect(() => {
    if (!units.length) return;
    const key = `${chapterId}|${at || ''}`;
    if (placed.current === key) return;
    placed.current = key;
    let code = pageOfAt;
    if (!code && !qmode) {
      const last = readLast(chapterId);
      code = last && units.some((u) => u.code === last) ? last : null;
    }
    setRevealAll(Boolean(pageOfAt) || Boolean(qmode));
    setActive(code || units[0].code);
  }, [units, at, chapterId, qmode, pageOfAt]);

  // after the page is drawn: go to the linked point (deep link), else the top
  const pendingAt = useRef(at || null);
  useEffect(() => { pendingAt.current = at || null; }, [at]);
  useLayoutEffect(() => {
    if (!active) return;
    const target = pendingAt.current;
    pendingAt.current = null;
    const el = target ? (document.getElementById(ID.point(target)) || document.getElementById(ID.unit(target))) : null;
    if (el && target !== active) scrollToEl(el, !qmode, headH.current);
    else window.scrollTo(0, 0);
  }, [active, qmode]);

  const goTo = useCallback((code) => {
    setRevealAll(false);
    setAddingFor(null);
    setActive(code);
    window.scrollTo(0, 0);
  }, []);

  const pageIdx = useMemo(() => units.findIndex((u) => u.code === active), [units, active]);
  const allSlides = useMemo(() => chapterSlides(deck, units), [deck, units]);
  const closePresenter = useCallback((slide) => {
    setPresentAt(null);
    if (!slide) return;
    setSlideAt(slide.id);
    changeView('slides');
    if (slide.unit && slide.unit !== active) goTo(slide.unit);
  }, [active, goTo, changeView]);
  const prevUnit = pageIdx > 0 ? units[pageIdx - 1] : null;
  const nextUnit = pageIdx >= 0 && pageIdx < units.length - 1 ? units[pageIdx + 1] : null;

  // ← / → keys turn the page (not while typing or while words are selected)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const u = e.key === 'ArrowLeft' ? prevUnit : nextUnit;
      if (u) { e.preventDefault(); goTo(u.code); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevUnit, nextUnit, goTo]);

  const jumpToPoint = useCallback((code) => {
    scrollToEl(document.getElementById(ID.point(code)), true, headH.current);
  }, []);

  // "Add stick-on" in the top strip: the unit on the page
  const addStickyHere = useCallback(() => {
    const code = active || (units[0] && units[0].code);
    if (!code) return;
    setAddingFor(code);
    window.scrollTo(0, 0);
  }, [active, units]);

  if (isLoading) {
    return (
      <>
        {qmode && <BackBar qmode={qmode} />}
        <Loading style={{ padding: '28px 32px' }} />
      </>
    );
  }
  if (isError || !data) {
    const notOpen = error && error.response && error.response.status === 404;
    return (
      <>
        {qmode && <BackBar qmode={qmode} />}
        <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          {notOpen
            ? <div style={{ fontSize: 16, color: C.text }}>These notes are not open yet.</div>
            : <LoadError onRetry={refetch} />}
          {!qmode && <Link to="/app/notes" style={{ fontSize: 15 }}>See all chapters</Link>}
        </div>
      </>
    );
  }

  return (
    <div className="v2-notes" data-testid={qmode ? 'v2-qnotes' : undefined}
      style={{ display: 'flex', flexDirection: 'column', minHeight: qmode ? '100vh' : '100%' }}>
      <style>{NOTES_CSS + PAGE_CSS}</style>
      {(() => {
        const u = pageIdx >= 0 ? units[pageIdx] : null;
        const parent = u && u.parent ? units.find((x) => x.code === u.parent) : null;
        const stripProps = {
          chapter: data.chapter, onHighlight: doHighlight, onAddSticky: addStickyHere,
          unit: u, parentLabel: parent ? unitLabel(parent) : null, pageNo: pageIdx + 1, pageCount: units.length,
          prev: prevUnit, next: nextUnit, onGo: goTo, labelOf: unitLabel,
          stickies: u ? (stickiesBy[u.code] || []) : [], ops: stickyOps,
          deck: qmode ? null : deck || null, view: view === 'slides' && u && slidesOf(deck, u.code).length ? 'slides' : 'theory',
          onView: changeView, slidesCount: u ? slidesOf(deck, u.code).length : 0,
        };
        return qmode ? (
          <div ref={headRef} style={{ position: 'sticky', top: 0, zIndex: 5 }}>
            <BackBar qmode={qmode} />
            <TopStrip {...stripProps} sticky={false} search={false} />
          </div>
        ) : (
          <TopStrip {...stripProps} top={barH} />
        );
      })()}
      <div style={{ display: 'flex', flexWrap: 'wrap', flexGrow: 1, alignItems: 'stretch' }}>
        {!qmode && <Outline units={units} depth={depth} active={active} onGo={goTo} top={barH} seen={seen} />}
        <div data-testid="v2-notes-body" ref={bodyRef} onClick={onBodyClick} style={{
          flex: '999 1 420px', minWidth: 0, boxSizing: 'border-box', padding: '28px 36px 80px',
          background: 'linear-gradient(180deg,#F5F7FF 0%,#F8FAFC 320px)', fontFamily: FONT.notes, fontSize: 18, lineHeight: 1.6, color: C.text,
        }}>
          <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {units.length === 0 && <div style={{ color: C.muted }}>No notes in this chapter yet.</div>}
            {pageIdx >= 0 && (() => {
              const u = units[pageIdx];
              const parent = u.parent ? units.find((x) => x.code === u.parent) : null;
              return (
                <NotePage key={u.code} unit={u} chapterTitle={`Chapter ${data.chapter.ncert_no} · ${data.chapter.title}`}
                  parentLabel={parent ? unitLabel(parent) : null}
                  pageNo={pageIdx + 1} pageCount={units.length} prev={prevUnit} next={nextUnit}
                  onGo={goTo} onFinish={() => navigate('/app/notes')}
                  stickies={stickiesBy[u.code] || []} hlBy={hlBy}
                  adding={addingFor === u.code} ops={stickyOps} onPlay={playClip}
                  qbox={qbox} qmodeEnd={qmode ? qmode : null} onJump={jumpToPoint} revealAll={revealAll}
                  deck={qmode ? null : deck || null} view={view} onView={changeView}
                  slideAt={slideAt} onPresent={allSlides.length ? setPresentAt : undefined} />
              );
            })()}
          </div>
        </div>
      </div>
      {showWord
        ? <WordPopover pos={selPos} info={wordInfo} onHighlight={doHighlight} />
        : <SelectionButton pos={selPos} onHighlight={doHighlight} />}
      <HighlightMenu menu={hlMenu} onRemove={doRemoveHighlight} onClose={closeHlMenu} />
      <Toast msg={toast} />
      {presentAt && allSlides.length > 0 && (
        <SlidePresenter slides={allSlides} startId={presentAt}
          labelOf={(code) => { const u = units.find((x) => x.code === code); return u ? unitLabel(u) : ''; }}
          onClose={closePresenter} />
      )}
      {video && (
        <VideoModal clip={video.clip} chapterId={data.chapter.chapter_id} unitCode={video.unit.code}
          unitLabel={unitLabel(video.unit)} onClose={closeVideo} />
      )}
    </div>
  );
}
